import { PAGE_PATH } from '@/constants';
import { fetchActiveChannel, fetchGetDistributor, fetchSettings, fetchCustomNoticeSubscribeScenes } from '@/services';
import {
  fetchActiveOrderByShoppingTrolley,
  fetchGetSubscribeMessageTemplateId,
  fetchShareSettings,
  fetchGetWeappDynamicRouteConfig,
} from '@/services/apis';
import { SettingKey, ShareSettingPageKey } from '@/types';
import type { MpApp } from '@/types/global-business';
import { defineStore } from 'pinia';

interface State {
  /** matomo是否已完成初始化 */
  isMatomoReady: boolean;
  /** 全局订阅开关 */
  subscribeGlobalSwitch: boolean;
  /** 全局渠道配置信息 */
  channelConfig: AnyObject | null;
  /** 进入会员页之前的tabbar路由数据缓存 */
  beforeRouteToMemberTabbarCache: MpApp.BeforeRouteToMemberTabbarCacheType;
  /** 富文本页面携带数据 */
  richTextData: {
    pageTitle: string;
    data: any;
  };
  /** 弹窗打开计数器，用于禁止滚动穿透，大于0时禁止滚动 */
  popupOpenCount: number;
  /** 分销员数据 */
  distributorData: AnyObject | null;
  /** 分享分销数据解析 */
  shareDistributorData: MpApp.shareDistributor | null;
  /** 精选商品配置 */
  settingsConfig: AnyObject[];
  /** 通知支付未完成标识 */
  noticcNotPaymentCompleteFlag: boolean;
  /** 购物车角标数量 */
  shoppingTrolleyCount: number;
  /** 资源预览器选项 */
  resourcePreviewerOptions: MpApp.ResourcePreviewOptions;
  /** 模版id */
  subscribeMessageTemplateIds: AnyObject | null;
  /** 网络慢、异常标识 */
  networkErrorFlag: boolean;
  /** 通用异常提示弹窗 */
  abnormalPromptPopupConfig: {
    ref: AnyObject | null;
    message: string;
    maskClick?: boolean;
    callback?: null | (() => void);
  };
  /** 分享设置页面key */
  shareSettingPageKey: Map<ShareSettingPageKey, AnyObject>;
  /** tabbar和功能页面配置信息 */
  weappDynamicRouteConfig: {
    tabbarConfig: AnyObject[];
    eventTrackingConfig: AnyObject[];
    isGetDone: boolean;
  };
  tabbarHeight: number;
  /** 自定义订阅通知各场景待授权模版 */
  customNoticeSceneTemplates: Record<string, MpApp.CustomNoticeTemplate[]>;
}

export const useAppStore = defineStore('app', {
  state: (): State => ({
    isMatomoReady: false,
    subscribeGlobalSwitch: false,
    channelConfig: null,
    beforeRouteToMemberTabbarCache: {
      index: 0,
      pagePath: PAGE_PATH.home.route,
      text: '首页',
      isback: false,
    },
    richTextData: {
      pageTitle: '',
      data: null,
    },
    popupOpenCount: 0,
    distributorData: null,
    shareDistributorData: null,
    settingsConfig: [],
    noticcNotPaymentCompleteFlag: false,
    shoppingTrolleyCount: -1,
    resourcePreviewerOptions: {
      display: false,
      data: [],
      defaultIndex: 0,
    },
    subscribeMessageTemplateIds: null,
    networkErrorFlag: false,
    abnormalPromptPopupConfig: {
      ref: null,
      message: '',
      maskClick: false,
      callback: null,
    },
    shareSettingPageKey: new Map(),
    weappDynamicRouteConfig: {
      tabbarConfig: [],
      eventTrackingConfig: [],
      isGetDone: false,
    },
    tabbarHeight: 0,
    customNoticeSceneTemplates: {},
  }),

  getters: {
    /** 是否禁止滚动（兼容旧代码） */
    displayPageMeta(): boolean {
      return this.popupOpenCount > 0;
    },
  },

  actions: {
    /** 增加弹窗打开计数 */
    incrementPopupCount() {
      this.popupOpenCount++;
    },
    /** 减少弹窗打开计数 */
    decrementPopupCount() {
      if (this.popupOpenCount > 0) {
        this.popupOpenCount--;
      }
    },
    /**
     * 获取全局渠道配置信息
     */
    async getShopConfigs(refresh?: boolean) {
      if (!this.channelConfig || refresh) {
        const res = await fetchActiveChannel();
        if (res) this.channelConfig = res;
      }
      return this.channelConfig;
    },
    /** 获取分销员数据 */
    async getGetDistributor(refresh?: boolean) {
      if (!this.distributorData || refresh) {
        const data = await fetchGetDistributor();
        if (data) this.distributorData = data;
      }
      return this.distributorData;
    },
    /** 获取精选商品配置 */
    async getSettings(refresh?: boolean) {
      if (!this.settingsConfig.length || refresh) {
        const data = await fetchSettings([
          SettingKey.memberCardProductGroup,
          SettingKey.personalCenterProductGroup,
          SettingKey.searchResultProductGroup,
          SettingKey.shoppingCartProductGroup,
          SettingKey.couponPageProductGroup,
          SettingKey.productDetailProductGroup,
          SettingKey.canBackProductDetailPage,
          SettingKey.forumRecommendProductGroup,
          SettingKey.shoppingMoneyGrantTiming,
        ]);
        if (data) this.settingsConfig = data;
      }
      return this.settingsConfig;
    },
    /** 获取分享页面设置 */
    async getShareSettingPageKey(pageKeys: ShareSettingPageKey[] = []) {
      const _keys = [
        ShareSettingPageKey.forumPost,
        ShareSettingPageKey.pointsMall,
        ShareSettingPageKey.pointsCheckin,
        ...pageKeys,
      ];
      if (!this.shareSettingPageKey.size) {
        const data = await fetchShareSettings(_keys);
        if (data)
          this.shareSettingPageKey = new Map(data.map((item: AnyObject) => [item.pageKey, item]));
      }
      console.log(`获取分享页面设置：`, this.shareSettingPageKey);

      return this.shareSettingPageKey;
    },
    /** 获取购物车角标数量 */
    async getShoppingTrolleyCount() {
      const data = await fetchActiveOrderByShoppingTrolley();
      if (data) {
        this.shoppingTrolleyCount = data.lines.length;
      }
    },
    /** 获取模版id集合 */
    async getSubscribeMessageTemplateId() {
      const data = await fetchGetSubscribeMessageTemplateId();
      if (data) {
        this.subscribeMessageTemplateIds = data;
      }
    },
    /** 获取tabbar和功能页面配置信息 */
    async getWeappDynamicRouteConfig() {
      try {
        if (this.weappDynamicRouteConfig.isGetDone) return
        const data = await fetchGetWeappDynamicRouteConfig();
        if (data) {
          this.weappDynamicRouteConfig = {
            ...data,
            isGetDone: true
          };
        } else {
          this.weappDynamicRouteConfig.isGetDone = true;
        }
      } catch (error) {
        this.weappDynamicRouteConfig.isGetDone = true;
      }
    },
    /** 获取全部场景的自定义订阅通知配置 */
    async getCustomNoticeSubscribeScenes() {
      try {
        const data = await fetchCustomNoticeSubscribeScenes();
        const templates: Record<string, MpApp.CustomNoticeTemplate[]> = {};
        (data || []).forEach((item: AnyObject) => {
          templates[item.scene] = item.enabled ? item.templates || [] : [];
        });
        this.customNoticeSceneTemplates = templates;
      } catch (error) {
        console.error('获取自定义订阅通知配置失败', error);
      }
    },
    /**
     * 移除已记账的自定义订阅通知模版
     * @param ids - 服务端模版主键集合
     */
    removeCustomNoticeTemplates(ids: string[]) {
      Object.keys(this.customNoticeSceneTemplates).forEach((scene) => {
        this.customNoticeSceneTemplates[scene] = this.customNoticeSceneTemplates[scene].filter(
          (item) => !ids.includes(item.id)
        );
      });
    },

    /**
     * 从 AppConfigInit 合并查询结果批量设置数据
     * 用于场景一优化：减少串行请求
     */
    setFromAppConfigInit(data: {
      settings?: AnyObject[];
      activeChannel?: AnyObject;
      getWeappDynamicRouteConfig?: AnyObject;
    }) {
      // 设置精选商品配置
      if (data.settings) {
        this.settingsConfig = data.settings;
      }

      // 设置渠道配置
      if (data.activeChannel) {
        this.channelConfig = data.activeChannel;
      }

      // 设置 tabbar 和功能页面配置
      if (data.getWeappDynamicRouteConfig) {
        this.weappDynamicRouteConfig = {
          tabbarConfig: data.getWeappDynamicRouteConfig.tabbarConfig || [],
          eventTrackingConfig: data.getWeappDynamicRouteConfig.eventTrackingConfig || [],
          isGetDone: true,
        };
      }
    },

    /**
     * 从 UserDataInit 合并查询结果设置分销员数据
     */
    setDistributorFromInit(data: AnyObject | null) {
      if (data) {
        this.distributorData = data;
      }
    },
  },
});
